/**
 * Specialist detail — charter, run history, grounded artifacts, and (spec
 * #47 §3a) its Setup & goal card.
 *
 * The card is the specialist half of the same surface the persona
 * workspace carries: `cadence_override` plus the `SpecialistConfig` the
 * onboarding interview writes. Before it, those fields had no editor at
 * all — the PATCH endpoint shipped with no caller.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonPage,
  Text,
} from '@shopify/polaris';

import {
  getArtifact,
  listArtifacts,
  listSpecialistRuns,
  listSpecialists,
  runSpecialist,
  setSpecialistEnabled,
} from '../services/marketingSuiteApi';
import type {
  Artifact,
  ArtifactSummary,
  Specialist,
  SpecialistRun,
} from '../types/marketingSuite';
import { ArtifactMedia } from '../components/marketingSuite/ArtifactMedia';
import { SimpleMarkdown } from '../components/marketingSuite/SimpleMarkdown';
import { SpecialistSetupCard } from '../components/workspace/setup/SpecialistSetupCard';

function groundingBadge(status: string) {
  switch (status) {
    case 'grounded':
      return <Badge tone="success">Grounded (LLM)</Badge>;
    case 'deterministic':
      return <Badge tone="info">Deterministic</Badge>;
    case 'failed_fallback':
      return <Badge tone="attention">Narration rejected — fallback</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

export default function MarketingAgentDetail() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const [specialist, setSpecialist] = useState<Specialist | null>(null);
  const [runs, setRuns] = useState<SpecialistRun[]>([]);
  const [artifacts, setArtifacts] = useState<ArtifactSummary[]>([]);
  const [selected, setSelected] = useState<Artifact | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Request generation: navigating A → B while A's requests are in
  // flight must not let A's slower responses overwrite B's rendered
  // state (PR #11 review). Every await re-checks the generation before
  // applying results.
  const loadSeq = useRef(0);
  // The page never unmounts across /marketing-agents/A → /B — only the
  // route param changes. Actions and artifact selections started under
  // A hold A-scoped closures; each re-checks these refs before applying
  // results or triggering an A-scoped reload (PR #11 review).
  const activeKey = useRef(key);
  const selectSeq = useRef(0);
  useEffect(() => {
    activeKey.current = key;
  }, [key]);

  // The key whose data is currently ON SCREEN. Navigating A → B must
  // blank A's page immediately: without the reset, A stayed rendered
  // under B's URL until every request settled — and stayed FOREVER when
  // B's catalog request rejected, because the catch only raised an
  // error banner over A's content (PR #11 review).
  const renderedKey = useRef<string | null>(null);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (renderedKey.current !== key) {
      renderedKey.current = key;
      setLoading(true);
      setSpecialist(null);
      setRuns([]);
      setArtifacts([]);
      setSelected(null);
      setError(null);
    }
    try {
      // allSettled: a run-history or artifact failure must not hide the
      // charter and the enable/disable controls when the catalog loaded
      // fine (PR #11 review) — same partial degradation as Dashboards.
      const [catalogResult, runResult, artifactResult] =
        await Promise.allSettled([
          listSpecialists(),
          listSpecialistRuns(key),
          listArtifacts(key, 20),
        ]);
      if (seq !== loadSeq.current) return;
      if (catalogResult.status === 'rejected') throw catalogResult.reason;
      setSpecialist(
        catalogResult.value.specialists.find((s) => s.key === key) ?? null
      );
      // Clear on rejection: keeping the previous value rendered agent
      // A's run history under agent B's name when only B's runs request
      // failed (PR #11 review).
      setRuns(runResult.status === 'fulfilled' ? runResult.value : []);
      const artifactList =
        artifactResult.status === 'fulfilled' ? artifactResult.value : [];
      setArtifacts(artifactList);
      if (artifactList.length) {
        const artifact = await getArtifact(artifactList[0].id);
        if (seq !== loadSeq.current) return;
        setSelected(artifact);
      } else {
        setSelected(null);
      }
      setError(null);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load specialist');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    void load();
  }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      await runSpecialist(key);
      // Reload only while still on this specialist: the A-scoped
      // load() would otherwise start a NEWER generation — discarding
      // B's in-flight load and rendering A's details under B's URL
      // (PR #11 review).
      if (activeKey.current === key) await load();
    } catch (e) {
      if (activeKey.current === key) {
        setError(e instanceof Error ? e.message : 'Run failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    if (!specialist) return;
    setBusy(true);
    try {
      await setSpecialistEnabled(key, !specialist.enabled);
      if (activeKey.current === key) await load();
    } catch (e) {
      if (activeKey.current === key) {
        setError(e instanceof Error ? e.message : 'Toggle failed');
      }
    } finally {
      setBusy(false);
    }
  };

  // Artifact selection is a standalone request outside load()'s
  // generation: without its own ordering, selecting under A and then
  // navigating (or re-loading) let A's slow response overwrite the
  // newer view's selection (PR #11 review). A newer selection OR a
  // newer load generation both invalidate the response.
  const selectArtifact = (id: string) => {
    const seq = ++selectSeq.current;
    const generation = loadSeq.current;
    void getArtifact(id)
      .then((artifact) => {
        if (seq !== selectSeq.current || generation !== loadSeq.current) {
          return;
        }
        setSelected(artifact);
      })
      .catch((e) => {
        if (seq !== selectSeq.current || generation !== loadSeq.current) {
          return;
        }
        setError(
          e instanceof Error ? e.message : 'Failed to load artifact'
        );
      });
  };

  if (loading) {
    return (
      <SkeletonPage title="Specialist">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </SkeletonPage>
    );
  }

  if (!specialist) {
    // A failed load must read as a failure, not as a bad URL.
    return (
      <Page
        title={error ? 'Could not load specialist' : 'Specialist not found'}
        backAction={{ onAction: () => navigate('/marketing-agents') }}
      >
        <Banner tone="critical">
          {error ?? `Unknown specialist “${key}”.`}
        </Banner>
      </Page>
    );
  }

  const runRows = runs.map((run) => [
    run.started_at ? new Date(run.started_at).toLocaleString() : '—',
    run.trigger,
    run.status,
    String((run.stats as Record<string, unknown>)?.facts ?? '—'),
    run.error ? run.error.slice(0, 80) : '',
  ]);

  return (
    <Page
      title={specialist.name}
      subtitle={specialist.mission}
      backAction={{ onAction: () => navigate('/marketing-agents') }}
      primaryAction={{
        content: 'Run now',
        onAction: () => void runNow(),
        loading: busy,
      }}
      secondaryActions={[
        {
          content: specialist.enabled ? 'Disable' : 'Enable',
          onAction: () => void toggle(),
          disabled: busy,
        },
      ]}
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        <InlineStack gap="200">
          <Badge>{specialist.effective_cadence}</Badge>
          <Badge tone={specialist.enabled ? 'success' : undefined}>
            {specialist.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
          {specialist.requires_sandbox && (
            <Badge tone="attention">Sandbox recipe</Badge>
          )}
          {specialist.required_connectors.map((c) => (
            <Badge key={c} tone="info">
              {c}
            </Badge>
          ))}
        </InlineStack>
        <Layout>
          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              {/* spec #47 §3a — the specialist's own Setup & goal. */}
              <SpecialistSetupCard specialistKey={key} />
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingMd">
                    Questions this agent owns
                  </Text>
                  {specialist.charter.map((q, i) => (
                    <Text as="p" key={i} tone="subdued">
                      • {q}
                    </Text>
                  ))}
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingMd">
                    Artifacts
                  </Text>
                  {artifacts.length === 0 && (
                    <Text as="p" tone="subdued">
                      No artifacts yet — run the agent.
                    </Text>
                  )}
                  {artifacts.map((a) => (
                    <Button
                      key={a.id}
                      variant={selected?.id === a.id ? 'primary' : 'tertiary'}
                      size="slim"
                      onClick={() => selectArtifact(a.id)}
                    >
                      {`${new Date(a.created_at).toLocaleDateString()} — ${a.artifact_type.replace(/_/g, ' ')}`}
                    </Button>
                  ))}
                </BlockStack>
              </Card>
            </BlockStack>
          </Layout.Section>
          <Layout.Section>
            <BlockStack gap="400">
              {selected && (
                <Card>
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h3" variant="headingMd">
                        {selected.title}
                      </Text>
                      {groundingBadge(selected.grounding_status)}
                    </InlineStack>
                    <SimpleMarkdown text={selected.summary_md} />
                  </BlockStack>
                </Card>
              )}
              {selected && <ArtifactMedia artifact={selected} />}
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingMd">
                    Run history
                  </Text>
                  {runRows.length ? (
                    <DataTable
                      columnContentTypes={[
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                      ]}
                      headings={['Started', 'Trigger', 'Status', 'Facts', 'Error']}
                      rows={runRows}
                    />
                  ) : (
                    <Text as="p" tone="subdued">
                      No runs yet.
                    </Text>
                  )}
                </BlockStack>
              </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </BlockStack>
    </Page>
  );
}
